import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { validatedApi } from '../../../lib/api/validated-client';
import type {
  ReviewResponse,
  PaginatedReviewsResponse,
  CreateReview,
  UpdateReview,
  ReviewFilter,
  AdminReviewFilter,
} from '@lov/shared';

/**
 * Get reviews for a specific venue (public)
 */
export function useVenueReviews(venueId: string, params: Partial<ReviewFilter> = {}) {
  return useQuery<PaginatedReviewsResponse>({
    queryKey: ['reviews', 'venue', venueId, params],
    queryFn: async () => {
      return validatedApi.get(`/reviews/venue/${venueId}`, { params });
    },
    enabled: !!venueId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get current user's reviews (authenticated)
 */
export function useMyReviews(params: Partial<ReviewFilter> = {}) {
  return useQuery<PaginatedReviewsResponse>({
    queryKey: ['reviews', 'my', params],
    queryFn: async () => {
      return validatedApi.get('/reviews/my', { params });
    },
  });
}

/**
 * Get a single review by ID
 */
export function useReview(id: string) {
  return useQuery<ReviewResponse>({
    queryKey: ['reviews', id],
    queryFn: async () => {
      return validatedApi.get(`/reviews/${id}`);
    },
    enabled: !!id,
  });
}

/**
 * Create a new review (authenticated)
 */
export function useCreateReview() {
  const queryClient = useQueryClient();

  return useMutation<ReviewResponse, Error, CreateReview>({
    mutationFn: async (reviewData: CreateReview) => {
      return validatedApi.post('/reviews', reviewData);
    },
    onSuccess: (data: ReviewResponse) => {
      // Invalidate venue reviews
      void queryClient.invalidateQueries({ queryKey: ['reviews', 'venue', data.venueId] });
      // Invalidate my reviews
      void queryClient.invalidateQueries({ queryKey: ['reviews', 'my'] });
      // Invalidate venue details (for rating stats update)
      void queryClient.invalidateQueries({ queryKey: ['venues', data.venueId] });
    },
  });
}

/**
 * Update an existing review (authenticated)
 */
export function useUpdateReview() {
  const queryClient = useQueryClient();

  return useMutation<ReviewResponse, Error, { id: string; data: UpdateReview }>({
    mutationFn: async ({ id, data }: { id: string; data: UpdateReview }) => {
      return validatedApi.patch(`/reviews/${id}`, data);
    },
    onSuccess: (data: ReviewResponse, variables) => {
      // Invalidate specific review
      void queryClient.invalidateQueries({ queryKey: ['reviews', variables.id] });
      // Invalidate venue reviews
      void queryClient.invalidateQueries({ queryKey: ['reviews', 'venue', data.venueId] });
      // Invalidate my reviews
      void queryClient.invalidateQueries({ queryKey: ['reviews', 'my'] });
      // Invalidate venue details (for rating stats update)
      void queryClient.invalidateQueries({ queryKey: ['venues', data.venueId] });
    },
  });
}

/**
 * Delete a review (authenticated)
 */
export function useDeleteReview() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; venueId: string }>({
    mutationFn: async ({ id }: { id: string; venueId: string }) => {
      await validatedApi.delete(`/reviews/${id}`);
    },
    onSuccess: (_data, variables) => {
      // Invalidate specific review
      void queryClient.invalidateQueries({ queryKey: ['reviews', variables.id] });
      // Invalidate venue reviews (targeted)
      void queryClient.invalidateQueries({ queryKey: ['reviews', 'venue', variables.venueId] });
      // Invalidate my reviews
      void queryClient.invalidateQueries({ queryKey: ['reviews', 'my'] });
      // Invalidate specific venue details (for rating stats update)
      void queryClient.invalidateQueries({ queryKey: ['venues', variables.venueId] });
    },
  });
}

// ============================================
// ADMIN HOOKS
// ============================================

/**
 * [ADMIN] Get all reviews with filtering (including unapproved)
 */
export function useAdminReviews(params: Partial<AdminReviewFilter> = {}) {
  return useQuery<PaginatedReviewsResponse>({
    queryKey: ['admin', 'reviews', params],
    queryFn: async () => {
      return validatedApi.get('/reviews/admin/all', { params });
    },
    staleTime: 1000 * 30, // 30 seconds (shorter cache for admin data)
  });
}

/**
 * [ADMIN] Approve a review
 */
export function useApproveReview() {
  const queryClient = useQueryClient();

  return useMutation<ReviewResponse, Error, string>({
    mutationFn: async (id: string) => {
      return validatedApi.patch(`/reviews/admin/${id}/approve`);
    },
    onSuccess: (data: ReviewResponse) => {
      // Invalidate admin reviews list
      void queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      // Invalidate venue reviews (now approved, will appear in public list)
      void queryClient.invalidateQueries({ queryKey: ['reviews', 'venue', data.venueId] });
      // Invalidate venue details (for rating stats update)
      void queryClient.invalidateQueries({ queryKey: ['venues', data.venueId] });
    },
  });
}

/**
 * [ADMIN] Reject/hide a review
 */
export function useRejectReview() {
  const queryClient = useQueryClient();

  return useMutation<ReviewResponse, Error, string>({
    mutationFn: async (id: string) => {
      return validatedApi.patch(`/reviews/admin/${id}/reject`);
    },
    onSuccess: (data: ReviewResponse) => {
      // Invalidate admin reviews list
      void queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      // Invalidate venue reviews (will be removed from public list)
      void queryClient.invalidateQueries({ queryKey: ['reviews', 'venue', data.venueId] });
      // Invalidate venue details (for rating stats update)
      void queryClient.invalidateQueries({ queryKey: ['venues', data.venueId] });
    },
  });
}

/**
 * [ADMIN] Flag a review as reported
 */
export function useFlagReview() {
  const queryClient = useQueryClient();

  return useMutation<ReviewResponse, Error, string>({
    mutationFn: async (id: string) => {
      return validatedApi.patch(`/reviews/admin/${id}/flag`);
    },
    onSuccess: () => {
      // Invalidate admin reviews list
      void queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
    },
  });
}
